import { computed, inject, InjectionToken } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals'
import { Events, withEventHandlers } from '@ngrx/signals/events'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { exhaustMap, map, mergeMap, pipe, tap } from 'rxjs'

import { nFirstItems } from '@app/core/array/n-first-items'
import { toggle } from '@app/core/array/toggle'
import { uniqBy } from '@app/core/array/uniq-by'
import { pluralize } from '@app/core/pluralize'
import { _Record } from '@app/core/record'
import { ConversationStore } from '@app/modules/messaging/components/store/conversation.store'
import { photosToSendMessagesPayload } from '@app/modules/messaging/components/store/sending.feature.utils'
import type { Conversation } from '@app/modules/messaging/models/conversation.model'
import type { MessageForm } from '@app/modules/messaging/models/message-form.model'
import { ConversationUIService } from '@app/modules/messaging/services/conversation-ui.service'
import type { BatchFileUploadErrorSummary } from '@app/modules/upload/core'
import { PopoverService } from '@app/overlays/popover/popover.service'
import { ToastService } from '@app/overlays/toast/toast.service'
import { ErrorHandlers } from '@app/services/error-handlers.service'
import { MemberService } from '@app/services/member.service'

import type { GetPhotosResponse, MessagePhotoAlbumSection } from '../../models/api.model'
import { MESSAGE_PHOTO_ALBUM_SECTIONS } from '../../models/api.model'
import type { MessagePhotoAlbumPhoto } from '../../models/message-photo-album-photo.model'
import { MessagePhotoAlbumService } from '../../services/message-photo-album.service'
import { photoUploadEvents } from '../../store/photo-upload.events'

interface SectionState {
  photos: MessagePhotoAlbumPhoto[]
  page: number
  has_next_page: boolean
}

const initialSectionState: SectionState = {
  photos: [],
  page: 1,
  has_next_page: false,
}

type State = Record<MessagePhotoAlbumSection, SectionState> & {
  activeSectionName: MessagePhotoAlbumSection
  editing: boolean
  busy: boolean
  initialized: boolean
  photoIdsToSend: number[]
  photoIdsToEdit: number[]
  error: BatchFileUploadErrorSummary | undefined
}

export interface MessagePhotoAlbumConfig {
  messageForm?: MessageForm
  conversation: Conversation
  initialSection: MessagePhotoAlbumSection
}

export const MESSAGE_PHOTO_ALBUM_CONFIG = new InjectionToken<MessagePhotoAlbumConfig>('MESSAGE_PHOTO_ALBUM_CONFIG')

const MAX_PHOTOS_TO_SEND = 10

export const MessagePhotoAlbumStore = signalStore(
  withState(
    (): State => ({
      ..._Record.fromKeys(MESSAGE_PHOTO_ALBUM_SECTIONS, initialSectionState),
      activeSectionName: inject(MESSAGE_PHOTO_ALBUM_CONFIG).initialSection,
      editing: false,
      busy: false,
      initialized: false,
      photoIdsToSend: [],
      photoIdsToEdit: [],
      error: undefined,
    }),
  ),
  withComputed(store => {
    const selectedPhotoIds = computed(() => (store.editing() ? store.photoIdsToEdit() : store.photoIdsToSend()))
    const approvedPhotoState = computed(() => store.approved().photos)

    return {
      approvedPhotos: computed(() =>
        approvedPhotoState().map(
          photo =>
            ({
              ...photo,
              selected: selectedPhotoIds().includes(photo.id),
              type: 'photo',
            }) as const,
        ),
      ),
      pendingPhotos: computed(() => store.pending().photos.map(photo => ({ ...photo, type: 'photo' }) as const)),
      canLoadMore: computed(() => store[store.activeSectionName()]().has_next_page),
      photosToSend: computed(() =>
        store.photoIdsToSend().map(id => approvedPhotoState().find(photo => photo.id === id)!),
      ),
    }
  }),
  withMethods(
    (
      store,
      messagePhotoAlbumService = inject(MessagePhotoAlbumService),
      conversationUIService = inject(ConversationUIService),
      popoverService = inject(PopoverService),
      conversationStore = inject(ConversationStore),
      config = inject(MESSAGE_PHOTO_ALBUM_CONFIG),
      memberService = inject(MemberService),
    ) => {
      const startEditing = () => patchState(store, { editing: true })
      const stopEditing = () => patchState(store, { editing: false, photoIdsToEdit: [] })

      const togglePhotoSelection = (id: number) =>
        patchState(store, state => ({
          ...(state.editing
            ? { photoIdsToEdit: toggle(state.photoIdsToEdit, id) }
            : {
                photoIdsToSend: nFirstItems(toggle(state.photoIdsToSend, id), MAX_PHOTOS_TO_SEND),
              }),
        }))

      const clearError = () => patchState(store, { error: undefined })

      const onLoadSuccess = (responsesMap: Record<MessagePhotoAlbumSection, GetPhotosResponse>): void => {
        patchState(store, {
          ..._Record.map(responsesMap, response => ({
            page: 1,
            photos: uniqBy([...response.message_album_photos], 'id'),
            has_next_page: response.total_pages > 1,
          })),
        })
      }

      const load = rxMethod<void>(
        pipe(
          exhaustMap(() => {
            patchState(store, { busy: true })
            return messagePhotoAlbumService.load().pipe(
              tapResponse({
                next: responsesMap => onLoadSuccess(responsesMap),
                error: (error: any) => ErrorHandlers.handleError(error),
                complete: () => patchState(store, { busy: false }),
                finalize: () => patchState(store, { initialized: true }),
              }),
            )
          }),
        ),
      )

      const onLoadMoreSuccess = (response: GetPhotosResponse, type: MessagePhotoAlbumSection, page: number): void => {
        patchState(store, state => ({
          [type]: {
            page,
            photos: uniqBy([...state[type].photos, ...response.message_album_photos], 'id'),
            has_next_page: response.total_pages > page,
          },
        }))
      }

      const loadMore = rxMethod<void>(
        pipe(
          mergeMap(() => {
            patchState(store, { busy: true })
            const type = store.activeSectionName()
            const page = store[type]().page + 1
            return messagePhotoAlbumService.getPhotos({ type, page }).pipe(
              tapResponse({
                next: response => onLoadMoreSuccess(response, type, page),
                error: (error: any) => ErrorHandlers.handleError(error),
                complete: () => patchState(store, { busy: false }),
              }),
            )
          }),
        ),
      )

      const onDeletePhotosSuccess = (selectedPhotoIds: number[]): void => {
        popoverService.show('nc-trash-can')
        patchState(store, state => ({
          editing: false,
          photoIdsToSend: state.photoIdsToSend.filter(id => !selectedPhotoIds.includes(id)),
          photoIdsToEdit: [] as number[],
          approved: {
            ...state.approved,
            photos: state.approved.photos.filter(photo => !selectedPhotoIds.includes(photo.id)),
          },
        }))
        if (store.approved().photos.length === 0 && store.approved().has_next_page) {
          load()
        }
      }

      const deletePhotos = rxMethod<void>(
        pipe(
          exhaustMap(() => {
            patchState(store, { busy: true })

            const photosToEdit = store.photoIdsToEdit()

            return messagePhotoAlbumService.removePhotos(photosToEdit).pipe(
              tapResponse({
                next: () => onDeletePhotosSuccess(photosToEdit),
                error: (error: any) => ErrorHandlers.handleError(error),
                complete: () => patchState(store, { busy: false }),
              }),
            )
          }),
        ),
      )

      const open = (section: MessagePhotoAlbumSection): void => {
        stopEditing()
        patchState(store, { activeSectionName: section })
      }

      const openDeleteConfirmModal = (): void => {
        const instance = conversationUIService.openMessagePhotoAlbumDeleteConfirmModal()
        instance.onConfirm(() => deletePhotos())
      }

      return {
        startEditing,
        stopEditing,
        togglePhotoSelection,
        clearError,
        load,
        loadMore,
        open,
        openDeleteConfirmModal,
        attemptToSendMessage: (photos: MessagePhotoAlbumPhoto[], messageForm: MessageForm | undefined): void => {
          const { payload, fakeMessages } = photosToSendMessagesPayload(
            'approved',
            photos,
            messageForm,
            memberService.account.photos_expiration_hours,
          )
          conversationStore.attemptToSendMessage(config.conversation, payload, fakeMessages)
        },
      }
    },
  ),
  withEventHandlers(
    (
      store,
      conversationStore = inject(ConversationStore),
      config = inject(MESSAGE_PHOTO_ALBUM_CONFIG),
      events = inject(Events),
      toastService = inject(ToastService),
      memberService = inject(MemberService),
    ) => ({
      onUploadEnd$: events.on(photoUploadEvents.uploadComplete).pipe(
        map(({ payload }) => payload),
        tap(({ photos, batchUpload, messageForm }) => {
          const error = batchUpload.getErrorSummary()
          if (photos.length === 0) {
            return patchState(store, { error })
          }
          const { payload, fakeMessages } = photosToSendMessagesPayload(
            'pending',
            photos,
            messageForm,
            memberService.account.photos_expiration_hours,
          )
          conversationStore.attemptToSendMessage(config.conversation, payload, fakeMessages)
          if (error) {
            toastService.show(`${pluralize(error.count, 'Photo')} not added to album. ${error.messages.join(' ')}`)
          }
        }),
      ),
    }),
  ),
  withHooks({
    onInit: store => {
      store.load()
    },
  }),
)
